/**
 * CoPilot Context Provider
 * =========================
 * Global context for sharing page-specific data with CoPilot.
 * Each page can set its context (metrics, current view, etc.)
 * and CoPilot will include this in queries to the LLM.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// --- Types ---
export interface PageMetric {
    name: string;
    value: string | number;
    unit?: string;
}

export interface CoPilotPageContext {
    pageName: string;
    pageDescription?: string;
    currentTab?: string;
    metrics?: PageMetric[];
    alerts?: string[];
    selectedAirport?: string;
    timeRange?: string;
    additionalData?: Record<string, unknown>;
}

interface CoPilotContextType {
    pageContext: CoPilotPageContext | null;
    setPageContext: (context: CoPilotPageContext) => void;
    clearPageContext: () => void;
    getContextSummary: () => string;
}

const CoPilotContext = createContext<CoPilotContextType | null>(null);

// --- Provider ---
export const CoPilotProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [pageContext, setPageContextState] = useState<CoPilotPageContext | null>(null);

    const setPageContext = useCallback((context: CoPilotPageContext) => {
        setPageContextState(context);
    }, []);

    const clearPageContext = useCallback(() => {
        setPageContextState(null);
    }, []);

    // Generate a text summary for the LLM
    const getContextSummary = useCallback((): string => {
        if (!pageContext) return '';

        const parts: string[] = [];

        parts.push(`[Current Page: ${pageContext.pageName}]`);

        if (pageContext.pageDescription) {
            parts.push(`Description: ${pageContext.pageDescription}`);
        }

        if (pageContext.currentTab) {
            parts.push(`Active Tab: ${pageContext.currentTab}`);
        }

        if (pageContext.selectedAirport) {
            parts.push(`Selected Airport: ${pageContext.selectedAirport}`);
        }

        if (pageContext.timeRange) {
            parts.push(`Time Range: ${pageContext.timeRange}`);
        }

        if (pageContext.metrics && pageContext.metrics.length > 0) {
            const metricsStr = pageContext.metrics
                .map(m => `${m.name}: ${m.value}${m.unit ? ' ' + m.unit : ''}`)
                .join(', ');
            parts.push(`Visible Metrics: ${metricsStr}`);
        }

        if (pageContext.alerts && pageContext.alerts.length > 0) {
            parts.push(`Active Alerts: ${pageContext.alerts.join('; ')}`);
        }

        return parts.join('\n');
    }, [pageContext]);

    return (
        <CoPilotContext.Provider value={{ pageContext, setPageContext, clearPageContext, getContextSummary }}>
            {children}
        </CoPilotContext.Provider>
    );
};

// --- Hook ---
export const useCoPilotContext = (): CoPilotContextType => {
    const context = useContext(CoPilotContext);
    if (!context) {
        throw new Error('useCoPilotContext must be used within CoPilotProvider');
    }
    return context;
};

export default CoPilotContext;
